import { CustomerMailer } from '@/modules/Customer/types'
import { useTranslation } from 'react-i18next'
import MailerCard from './MailerCard'
import { Badge } from '@/components/ui/badge'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import { Mail, Users, Tag } from 'lucide-react'

interface GroupedMailerViewProps {
    groupedData: Record<string, CustomerMailer[]>
    mode: 'recipients' | 'purpose'
    onEdit: (mailer: CustomerMailer) => void
    onEmailClick?: (email: string) => void
    selectedEmail?: string | null
}

const GroupedMailerView: React.FC<GroupedMailerViewProps> = ({
    groupedData,
    mode,
    onEdit,
    onEmailClick,
    selectedEmail,
}) => {
    const { t } = useTranslation()

    const renderRecipientGroups = () => {
        return Object.entries(groupedData).map(([recipients, mailers]) => {
            const recipientList = recipients.split(',').map((e) => e.trim())
            const recipientCount = recipientList.length

            return (
                <Card key={recipients} className="overflow-hidden">
                    <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-1.5">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 shrink-0">
                                        <Users className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <CardTitle className="text-base">
                                            {t('recipient-group')}
                                        </CardTitle>
                                        <CardDescription className="flex items-center gap-2 mt-0.5">
                                            <Mail className="w-3 h-3" />
                                            <span>
                                                {recipientCount}{' '}
                                                {recipientCount === 1
                                                    ? t('recipient')
                                                    : t('recipients')}
                                            </span>
                                            <span className="text-muted-foreground/50">
                                                •
                                            </span>
                                            <span>
                                                {mailers.length}{' '}
                                                {mailers.length === 1
                                                    ? t('mailer')
                                                    : t('mailers')}
                                            </span>
                                        </CardDescription>
                                    </div>
                                </div>

                                {/* Recipients badges */}
                                <div className="flex flex-wrap gap-1.5 pt-2">
                                    {recipientList.map((email, i) => (
                                        <Badge
                                            key={i}
                                            variant={
                                                selectedEmail === email
                                                    ? 'default'
                                                    : 'secondary'
                                            }
                                            className={`cursor-pointer text-xs h-6 transition-all ${
                                                selectedEmail === email
                                                    ? 'ring-2 ring-ring/20'
                                                    : 'hover:bg-accent'
                                            }`}
                                            onClick={() =>
                                                onEmailClick?.(email)
                                            }
                                        >
                                            <Mail className="w-3 h-3 mr-1" />
                                            {email}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {mailers.map((mailer, index) => (
                                <MailerCard
                                    key={index}
                                    mailer={mailer}
                                    compact={true}
                                    onEdit={onEdit}
                                    onEmailClick={onEmailClick}
                                    selectedEmail={selectedEmail}
                                />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )
        })
    }

    const renderPurposeGroups = () => {
        return Object.entries(groupedData).map(([purpose, mailers]) => {
            const uniqueRecipients = Array.from(
                new Set(
                    mailers.flatMap(
                        (m) =>
                            m.mailer_receiver
                                ?.split(',')
                                .map((e) => e.trim()) || []
                    )
                )
            )

            return (
                <Card key={purpose} className="overflow-hidden">
                    <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-1.5">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 shrink-0">
                                        <Tag className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <CardTitle className="text-base">
                                            {purpose}
                                        </CardTitle>
                                        <CardDescription className="flex items-center gap-2 mt-0.5">
                                            <Mail className="w-3 h-3" />
                                            <span>
                                                {uniqueRecipients.length}{' '}
                                                {uniqueRecipients.length === 1
                                                    ? t('recipient')
                                                    : t('recipients')}
                                            </span>
                                            <span className="text-muted-foreground/50">
                                                •
                                            </span>
                                            <span>
                                                {mailers.length}{' '}
                                                {mailers.length === 1
                                                    ? t('mailer')
                                                    : t('mailers')}
                                            </span>
                                        </CardDescription>
                                    </div>
                                </div>

                                {/* Recipients badges */}
                                <div className="flex flex-wrap gap-1.5 pt-2">
                                    {uniqueRecipients
                                        .slice(0, 5)
                                        .map((email, i) => (
                                            <Badge
                                                key={i}
                                                variant={
                                                    selectedEmail === email
                                                        ? 'default'
                                                        : 'secondary'
                                                }
                                                className={`cursor-pointer text-xs h-6 transition-all ${
                                                    selectedEmail === email
                                                        ? 'ring-2 ring-ring/20'
                                                        : 'hover:bg-accent'
                                                }`}
                                                onClick={() =>
                                                    onEmailClick?.(email)
                                                }
                                            >
                                                <Mail className="w-3 h-3 mr-1" />
                                                {email}
                                            </Badge>
                                        ))}
                                    {uniqueRecipients.length > 5 && (
                                        <Badge
                                            variant="outline"
                                            className="text-xs h-6"
                                        >
                                            +{uniqueRecipients.length - 5}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {mailers.map((mailer, index) => (
                                <MailerCard
                                    key={index}
                                    mailer={mailer}
                                    compact={true}
                                    onEdit={onEdit}
                                    onEmailClick={onEmailClick}
                                    selectedEmail={selectedEmail}
                                />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )
        })
    }

    return (
        <div className="space-y-4">
            {mode === 'recipients'
                ? renderRecipientGroups()
                : renderPurposeGroups()}
        </div>
    )
}

export default GroupedMailerView
